import type { Passage } from "./types";

export const passage: Passage = {
  slug: "philippians-4-13",
  reference: "Philippians 4:13",
  title: "Philippians 4:13 meaning: what the all things are",
  h1: "Philippians 4:13, and what the “all things” are",
  description:
    "It is painted on gym walls and written on wristbands. Two verses earlier Paul says what he has learned to do: be content, hungry or full, from a prison cell.",
  targets: [
    "philippians 4:13 meaning",
    "i can do all things through christ context",
    "philippians 4 13 explained",
  ],
  published: "2026-08-27",
  standfirst:
    "The verse is about doing all things. The sentence before it says which things, and they are not the ones on the wristband.",
  verse: {
    text: "I can do all things through Christ, who strengthens me.",
    ref: "Philippians 4:13",
  },
  context: [
    {
      text: "Not that I speak because of lack, for I have learned in whatever state I am, to be content in it.",
      ref: "Philippians 4:11",
    },
    {
      text: "I know how to be humbled, and I also know how to abound. In any and all circumstances I have learned the secret both to be filled and to be hungry, both to abound and to be in need.",
      ref: "Philippians 4:12",
    },
  ],
  takeaways: [
    "Verse 12 lists the 'all things': being humbled and abounding, being filled and being hungry, having plenty and being in need.",
    "Paul says he learned it, twice, in two verses — this is a description of an acquired skill rather than of a temperament or a burst of strength.",
    "The letter was written from custody, to a church that had just sent him money, which is the situation the contentment is being claimed in.",
    "Read as a promise of achievement it is a verse for the day you win; read as written it is a verse for the day you do not, which is most days.",
  ],
  body: [
    {
      type: "h2",
      id: "the-sentence-before",
      text: "The sentence before it",
    },
    {
      type: "p",
      text: "Philippians 4:13 is the end of a paragraph, and the paragraph is about money and hunger. Paul is thanking the church at Philippi for a gift, and he is careful to say that he is not thanking them because he was desperate. He has learned, he says, to be content in whatever state he is in. Then verse 12 spells out the states: humbled and abounding, filled and hungry, plenty and need.",
    },
    {
      type: "p",
      text: "Verse 13 is the conclusion of that argument. The all things it refers to are the ones just listed. It is a claim about being able to handle both ends of a circumstance, not a claim about being able to accomplish whatever is attempted.",
    },
    {
      type: "h2",
      id: "learned",
      text: "The word he keeps using",
    },
    {
      type: "p",
      text: "Twice in two verses: I have learned. It is not a boast about the strength he was issued with; it is a report on a skill acquired over a long and eventful career, most of it uncomfortable. That word is the most encouraging thing on this page, because a skill can be acquired by somebody who does not currently have it.",
    },
    {
      type: "p",
      text: "The letter itself is written in custody, which the first chapter says plainly. Contentment claimed from a cell by a man thanking people for a food parcel is a different sentence from contentment claimed in a good year.",
    },
    {
      type: "h2",
      id: "why-it-became-a-slogan",
      text: "Why it became a slogan",
    },
    {
      type: "p",
      text: "Because the English of it detaches cleanly. Nine words, an absolute claim, and a subject who can be anybody. Sport in particular took it, and it now appears on wristbands, boots, and the walls of gyms, where it functions as a very short prayer for winning.",
    },
    {
      type: "p",
      text: "Nothing about that is malicious and most people quoting it have never seen verse 12. But the version that circulates has the opposite emotional direction from the one in the letter: the slogan is for the moment before the attempt, and the verse is for the season after it, when the attempt did not go well and the food has run short.",
    },
  ],
  misreading: {
    claim:
      "That it is a promise of capability — with Christ, you can achieve anything you set out to do.",
    correction: [
      "The immediate problem is that the surrounding sentences say something else and say it in a list. Verse 12 defines the all things as abundance and need, fullness and hunger. Verse 13 is the summary of that list, and reading it as unlimited achievement requires ignoring the clause it is attached to.",
      "The deeper problem is what the slogan does on the day it fails. A verse that guarantees outcomes is a verse that indicts the person the moment an outcome goes badly — and the people most likely to be holding it at that moment are the ones who took it most seriously. The letter's version cannot fail that way, because it was written by somebody whose circumstances had already gone badly and who was describing what he had learned inside them.",
      "There is a version of the popular reading that survives, and it is worth naming rather than dismissing. Paul does claim strength for the thing in front of him. What he does not claim is that the thing in front of him will succeed, and the difference between those two is the whole of the misreading.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Learn it with verse 12, as one unit. On its own it is a slogan and the slogan is already in your head; the pair is a different sentence and it is the one that holds on a bad week. {Learning-a-longer-passage} is not much harder than learning a single line — the review schedule is the same.",
      links: {
        "Learning-a-longer-passage": "/blog/how-to-memorize-bible-verses",
      },
    },
    {
      type: "p",
      text: "The other use is the one the paragraph is actually about. Contentment is a learned skill according to the man writing, which means it is trainable, which means the question is what the training consists of. That is a more interesting question than whether the verse can be printed on a wristband, and {ten-passages-on-comparison} is where most of the answer lives.",
      links: {
        "ten-passages-on-comparison": "/verses/bible-verses-about-comparison",
      },
    },
  ],
  faq: [
    {
      q: "What are the 'all things' in Philippians 4:13?",
      a: "Verse 12 lists them: being humbled and abounding, being filled and being hungry, having plenty and being in need. The verse is a summary of that list, so the all things are the range of circumstances rather than the range of achievements.",
    },
    {
      q: "Is it wrong to use Philippians 4:13 for sport or exams?",
      a: "Not wrong so much as narrow. Paul does claim strength for what is in front of him, so praying it before something difficult is fair. What the passage does not support is the implication that the outcome is therefore guaranteed — and that is the part that hurts on the day it goes badly.",
    },
    {
      q: "Where was Paul when he wrote it?",
      a: "In custody. Philippians says so in its first chapter, and the letter is largely a thank-you to a church that had sent him support. The contentment being described is being described from that position, which is what gives the claim its weight.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain. Translations differ slightly on verse 13 — some read 'him who strengthens me' — and none of them changes what verse 12 lists.",
    },
  ],
  related: [
    "how-to-memorize-bible-verses",
    "social-media-fast-guide",
    "morning-quiet-time-routine",
  ],
  relatedTopics: [
    "bible-verses-about-comparison",
    "bible-verses-about-self-control",
  ],
  relatedPassages: ["philippians-4-6-7", "jeremiah-29-11"],
};
